import { useContactForm } from '@/hooks/useContactForm';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 24px;
  max-width: 1440px;
  margin: 0 auto;
`;

const StyledForm = styled.form`
  width: 100%;
  max-width: 1020px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  .form-row {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;

    @media (min-width: 640px) {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  .full-width {
    width: 100%;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 60px;
  padding: 0 24px;
  border: 1px solid #566167ff;
  border-radius: 100px;
  font-family: inherit;
  font-size: 16px;
  color: #657d84;
  background: white;
  
  @media (min-width: 640px) {
    width: calc(50% - 16px);
  }

  @media (min-width: 1024px) {
    width: 480px;
  }
  
  &::placeholder {
    color: #657d84;
  }
  
  &:focus {
    outline: none;
    border-color: #657d84;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 60px;
  padding: 0 24px;
  border: 1px solid #566167ff;
  border-radius: 100px;
  font-family: inherit;
  font-size: 16px;
  color: #657d84;
  background: white;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23657d84%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22%2F%3E%3C%2Fsvg%3E');
  background-position: right 24px center;
  background-repeat: no-repeat;
  background-size: 12px 12px;
  
  @media (min-width: 640px) {
    width: calc(50% - 16px);
  }

  @media (min-width: 1024px) {
    width: 480px;
  }
  
  &:focus {
    outline: none;
    border-color: #657d84;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: 16px 24px;
  border: 1px solid #566167ff;
  border-radius: 24px;
  font-family: inherit;
  font-size: 16px;
  color: #657d84;
  background: white;
  resize: none;
  
  &::placeholder {
    color: #657d84;
  }
  
  &:focus {
    outline: none;
    border-color: #657d84;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 65px;
  background: #566167ff;
  color: white;
  border: none;
  border-radius: 100px;
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  max-width: 1020px;

  &:hover {
    background: #657d84ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff0000;
  font-size: 14px;
  margin-top: 4px;
`;

const MessageBanner = styled.div<{ type: 'success' | 'error' }>`
  width: 100%;
  max-width: 1020px;
  padding: 16px 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  font-size: 16px;
  text-align: center;
  animation: fadeIn 0.3s ease-in-out;

  ${({ type }) => type === 'success' ? `
    background-color: #e6f4ea;
    color: #1e4620;
    border: 1px solid #b7dfb9;
  ` : `
    background-color: #fce8e8;
    color: #c62828;
    border: 1px solid #fad2d2;
  `}

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 100px;
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #566167ff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const DateInput = ({ value, onChange, error }: { 
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const isValidDateFormat = (dateStr: string): boolean => {
    if (!/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return false;

    const [day, month, year] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    return (
      date.getDate() === day &&
      date.getMonth() === month - 1 &&
      date.getFullYear() === year
    );
  };

  const isPastDate = (dateStr: string): boolean => {
    if (!isValidDateFormat(dateStr)) return false;

    const [day, month, year] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    return date < today;
  };

  const formatDate = (input: string) => {
    // Remove all non-digits
    const numbers = input.replace(/\D/g, '');
    
    // Format as DD-MM-YYYY
    let formatted = '';
    if (numbers.length > 0) formatted += numbers.substring(0, 2);
    if (numbers.length > 2) formatted += '-' + numbers.substring(2, 4);
    if (numbers.length > 4) formatted += '-' + numbers.substring(4, 8);
    
    return formatted;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDate(e.target.value);
    setInputValue(formatted);
    onChange(formatted);
  };

  // Show error states
  const showFormatError = inputValue.length === 10 && !isValidDateFormat(inputValue);
  const showPastDateError = inputValue.length === 10 && isValidDateFormat(inputValue) && isPastDate(inputValue);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Input
        type="text"
        value={inputValue}
        onChange={handleChange}
        placeholder="DD-MM-YYYY"
        maxLength={10}
        aria-invalid={!!error || showFormatError || showPastDateError}
      />
      {showFormatError && (
        <ErrorMessage>Please enter a valid date in DD-MM-YYYY format</ErrorMessage>
      )}
      {showPastDateError && (
        <ErrorMessage>Wedding date cannot be in the past</ErrorMessage>
      )}
      {error && !showFormatError && !showPastDateError && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </div>
  );
};

export const ContactFormNew = () => {
  const navigate = useNavigate();
  const {
    form,
    isSubmitting,
    submitError,
    submitSuccess,
    onSubmit
  } = useContactForm();

  const { register, watch, setValue, formState: { errors } } = form;

  // Navigate to success page after successful submission
  useEffect(() => {
    if (submitSuccess) {
      navigate('/submission-success');
    }
  }, [submitSuccess, navigate]);

  return (
    <FormContainer>
      {submitError && (
        <MessageBanner type="error">
          {submitError}
        </MessageBanner>
      )}

      <StyledForm onSubmit={onSubmit}>
        <div className="form-row">
          <div style={{ position: 'relative', width: '100%' }}>
            <DateInput
              value={watch('wedding_date') || ''}
              onChange={(value) => setValue('wedding_date', value)}
              error={errors.wedding_date?.message}
            />
          </div>
          
          <div style={{ position: 'relative', width: '100%' }}>
            <Select
              {...register('budget')}
              aria-invalid={!!errors.budget}
            >
              <option value="">What is Your Budget?</option>
              <option value="Gold Package | £1,595 or £79 Per Month">Gold Package | £1,595 or £79 Per Month</option>
              <option value="Platinum Package | £1,995 or £89 Per Month">Platinum Package | £1,995 or £89 Per Month</option>
              <option value="Diamond Package | £2,595 or £99 Per Month">Diamond Package | £2,595 or £99 Per Month</option>
            </Select>
            {errors.budget && (
              <ErrorMessage>{errors.budget.message}</ErrorMessage>
            )}
          </div>
        </div>

        <div className="form-row">
          <Input
            type="text"
            {...register('first_name')}
            placeholder="Your First Name"
          />

          <Input
            type="text"
            {...register('partner_name')}
            placeholder="Partner's First Name"
          />
        </div>

        <div className="form-row">
          <Input
            type="email"
            {...register('email')}
            placeholder="Your Email"
          />

          <Input
            type="tel"
            {...register('mobile')}
            placeholder="Your Mobile"
          />
        </div>

        <div className="form-row">
          <Select {...register('ceremony_location')}>
            <option value="">Ceremony Location</option>
            <option value="Venue">Venue</option>
            <option value="Church">Church</option>
            <option value="Other">Other</option>
          </Select>

          <Select {...register('wedding_county')}>
            <option value="">Wedding County</option>
            <option value="Bedfordshire">Bedfordshire</option>
            <option value="Berkshire">Berkshire</option>
            <option value="Buckinghamshire">Buckinghamshire</option>
            <option value="Cambridgeshire">Cambridgeshire</option>
            <option value="Cheshire">Cheshire</option>
            <option value="Cornwall">Cornwall</option>
            <option value="Derbyshire">Derbyshire</option>
            <option value="Devon">Devon</option>
            <option value="Dorset">Dorset</option>
            <option value="Essex">Essex</option>
            <option value="Gloucestershire">Gloucestershire</option>
            <option value="Hampshire">Hampshire</option>
            <option value="Herefordshire">Herefordshire</option>
            <option value="Hertfordshire">Hertfordshire</option>
            <option value="Kent">Kent</option>
            <option value="Lancashire">Lancashire</option>
            <option value="Lincolnshire">Lincolnshire</option>
            <option value="London">London</option>
            <option value="Norfolk">Norfolk</option>
            <option value="Northamptonshire">Northamptonshire</option>
            <option value="Nottinghamshire">Nottinghamshire</option>
            <option value="Oxfordshire">Oxfordshire</option>
            <option value="Rutland">Rutland</option>
            <option value="Shropshire">Shropshire</option>
            <option value="Somerset">Somerset</option>
            <option value="Staffordshire">Staffordshire</option>
            <option value="Suffolk">Suffolk</option>
            <option value="Surrey">Surrey</option>
            <option value="Sussex">Sussex</option>
            <option value="Warwickshire">Warwickshire</option>
            <option value="Wiltshire">Wiltshire</option>
            <option value="Worcestershire">Worcestershire</option>
            <option value="Yorkshire">Yorkshire</option>
            <option value="Other">Other</option>
          </Select>
        </div>

        {(watch('ceremony_location') === 'Other' || watch('wedding_county') === 'Other') && (
          <div className="form-row" style={{ justifyContent: 'space-between' }}>
            {watch('ceremony_location') === 'Other' && (
              <Input
                type="text"
                placeholder="Specify Ceremony Location"
                {...register('ceremony_location_other')}
                style={{ marginRight: 'auto' }}
              />
            )}
            {watch('wedding_county') === 'Other' && (
              <Input
                type="text"
                placeholder="Specify Wedding County"
                {...register('wedding_county_other')}
                style={{ marginLeft: watch('ceremony_location') === 'Other' ? '0' : 'auto' }}
              />
            )}
          </div>
        )}

        <div className="full-width">
          <TextArea
            {...register('message')}
            placeholder="Please tell us about your big day :)"
          />
        </div>

        <div style={{ position: 'relative', width: '100%' }}>
          <SubmitButton 
            type="submit" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <LoadingOverlay>
                <Spinner />
              </LoadingOverlay>
            ) : 'Submit Enquiry'}
          </SubmitButton>
        </div>
      </StyledForm>
    </FormContainer>
  );
}; 